import React from "react";

type SchoolLike = {
  schoolName?: string;
  name?: string;
  nameofSchool?: string;
  address?: string;
  village?: string;
  city?: string;
  district?: string;
  districtCode?: string;
  blockName?: string;
  block?: string;
  blockCode?: string;
  diseCode?: string;
  udiseCode?: string;
  schoolCode?: string;
  mpbseCode?: string;
  principalName?: string;
  headMasterName?: string;
  schoolPrincipalName?: string;
  principalDesignation?: string;
  principalMobile?: string;
  principalEmail?: string;
  pinNo?: string;
  state?: string;
  medium?: string[];
  jsk_aeo?: string;
  schoolLogo?: string;
  schoolImage?: string;
  mobile?: string;
  phone?: string;
  email?: string;
};

export interface SchoolRegisterCoverPageProps {
  school?: SchoolLike | null;
  academicYear?: string;
  className?: string;
}

const outerStyle: React.CSSProperties = {
  background: "#fff",
  margin: "10px auto",
  width: "297mm",
  maxWidth: "297mm",
  minHeight: "210mm",
  position: "relative",
  padding: "22px",
  boxSizing: "border-box",
  border: "3px solid #000",
  outline: "1px solid #000",
  display: "flex",
  flexDirection: "column",
  gap: "18px",
};

const titleWrapStyle: React.CSSProperties = {
  textAlign: "center",
  borderBottom: "2px solid #000",
  paddingBottom: "12px",
};

const titleStyle: React.CSSProperties = {
  fontSize: "28px",
  fontWeight: 800,
  letterSpacing: "0.5px",
  margin: 0,
  color: "#b22222",
  textTransform: "uppercase",
};

const subtitleStyle: React.CSSProperties = {
  marginTop: "8px",
  fontSize: "20px",
  fontWeight: 700,
};

const infoGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "15px 18px",
  fontSize: "14px",
  marginTop: "20px",
};

const labelStyle: React.CSSProperties = {
  fontWeight: 700,
  minWidth: "160px",
  display: "inline-block",
};

const rowStyle: React.CSSProperties = {
  border: "1px solid #000",
  padding: "10px 12px",
  minHeight: "44px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
};

const footerStyle: React.CSSProperties = {
  marginTop: "auto",
  display: "flex",
  justifyContent: "space-between",
  gap: "18px",
  fontSize: "13px",
};

const signBoxStyle: React.CSSProperties = {
  flex: 1,
  border: "1px solid #000",
  padding: "12px",
  minHeight: "90px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

function pick(...values: Array<unknown>) {
  for (const v of values) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
}

const SchoolRegisterCoverPage: React.FC<SchoolRegisterCoverPageProps> = ({
  school,
  academicYear = "",
  className = "",
}) => {
  const schoolName = pick(school?.nameofSchool, school?.schoolName, school?.name);
  const district = pick(school?.district);
  const districtCode = pick(school?.districtCode);
  const block = pick(school?.blockName, school?.block);
  const blockCode = pick(school?.blockCode);
  const diseCode = pick(school?.diseCode, school?.udiseCode);
  const mpbseCode = pick(school?.schoolCode, school?.mpbseCode);
  const principal = pick(school?.schoolPrincipalName, school?.principalName, school?.headMasterName);
  const principalDesignation = pick(school?.principalDesignation);
  const phone = pick(school?.principalMobile, school?.mobile, school?.phone);
  const email = pick(school?.principalEmail, school?.email);
  const address = pick(school?.address);
  const city = pick(school?.city);
  const village = pick(school?.village);
  const pinNo = pick(school?.pinNo);
  const state = pick(school?.state);
  const medium = Array.isArray(school?.medium) ? school?.medium.filter(Boolean).join(", ") : "";
  const jskAeo = pick(school?.jsk_aeo);

  const apiBase = String(process.env.REACT_APP_API_URL || "").trim();
  const logoFileName = pick(school?.schoolLogo);
  const schoolLogoUrl =
    apiBase && logoFileName ? `${apiBase}/schools/logos/${logoFileName}` : "/logo.png";

  const fullAddress = [address, village, city].filter(Boolean).join(", ");

  return (
    <div className="school-register-cover-page" style={outerStyle}>
      {/* Transparent school logo background (watermark) */}
      {schoolLogoUrl && (
        <img
          src={schoolLogoUrl}
          // alt="School Logo"
          style={{
            position: "absolute",
            inset: 0,
            margin: "auto",
            width: "70%",
            height: "70%",
            objectFit: "contain",
            opacity: 0.15,
            zIndex: 0,
            pointerEvents: "none",
          }}
          crossOrigin="anonymous"
        />
      )}
      <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: "none",
            background:
              "linear-gradient(to right, rgba(144, 238, 144, 0.15) 0%, rgba(255, 255, 153, 0.15) 50%, rgba(255, 160, 160, 0.15) 100%)",
          }}
        />

      {/* Content wrapper to ensure content appears above background logo */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "18px",
          flex: 1,
          minHeight: "100%",
        }}
      >
        <div style={titleWrapStyle}>
          <h2 style={titleStyle}>School Register</h2>
          <div style={subtitleStyle}>
            {schoolName || "—"}
          </div>
          <div style={{ marginTop: "6px", fontSize: "14px", fontWeight: 700 }}>
            Academic Year: {academicYear || "—"} {className ? ` | Class: ${className}` : ""}
          </div>
        </div>

        <div style={infoGridStyle}>
          <div style={rowStyle}>
            <span style={labelStyle}>Address:</span>
            <span>
              {fullAddress || ""}
              {pinNo ? `, PIN: ${pinNo}` : ""}
              {state ? `, ${state}` : ""}
            </span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Medium:</span>
            <span>{medium || "—"}</span>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>District:</span>
            <span>
              {district || " "}
              {districtCode ? ` (${districtCode})` : ""}
            </span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Block:</span>
            <span>
              {block || "—"}
              {blockCode ? ` (${blockCode})` : ""}
            </span>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>DISE Code:</span>
            <span>{diseCode || "—"}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>MPBSE Code:</span>
            <span>{mpbseCode || "—"}</span>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>JSK/AEO:</span>
            <span>{jskAeo || "—"}</span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>Contact:</span>
            <span>
              {phone || "—"}
              {email ? ` | ${email}` : ""}
            </span>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Principal:</span>
            <span>
              {principal || "—"}
              {principalDesignation ? ` (${principalDesignation})` : ""}
            </span>
          </div>
          <div style={rowStyle}>
            <span style={labelStyle}>DISE / MPBSE:</span>
            <span>
              {diseCode || "—"} {mpbseCode ? ` / ${mpbseCode}` : ""}
            </span>
          </div>
        </div>

        {/* <div style={footerStyle}>
          <div style={signBoxStyle}>
            <div style={{ fontWeight: 700 }}>Prepared By</div>
            <div style={{ textAlign: "right" }}>Signature</div>
          </div>
          <div style={signBoxStyle}>
            <div style={{ fontWeight: 700 }}>Verified By</div>
            <div style={{ textAlign: "right" }}>Signature</div>
          </div>
          <div style={signBoxStyle}>
            <div style={{ fontWeight: 700 }}>Principal / Head</div>
            <div style={{ textAlign: "right" }}>Seal & Signature</div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default SchoolRegisterCoverPage;

